/**
 * Everything this app is allowed to say about the network it is running on.
 *
 * Android's `NetworkStatus` (core/network/NetworkInfo.kt) carries four things:
 * the transport (`wifi`/`mobile`/`ethernet`/`vpn`), the cellular generation
 * (`4g`/`5g`), whether the connection is *validated* — i.e. actually reaches the
 * internet rather than a captive portal — and a carrier name. **Safari on iOS
 * exposes none of it.** There is no Network Information API there, no
 * `navigator.connection`, and nothing that distinguishes "connected" from
 * "connected to a hotel wifi that goes nowhere" (SPEC §14 #5).
 *
 * So this type has exactly one field, and it stays that way. A `kind: 'wifi'`
 * here would have to be guessed, and a guess about the network is the worst kind
 * to make in this app: `network` is one of the fields that labels every
 * submitted batch, and a plausible wrong value is indistinguishable from a
 * measured one once it reaches the backend. Same rule as `statusCode` staying
 * null — never fabricate a field.
 *
 * What `online: true` is worth is also strictly less than Android's
 * `hasInternet`. It means an interface exists; the uplink may still be dead.
 * `online: false` is the trustworthy direction, and it is the one the UI acts on
 * — see the same reasoning in `network.ts#isOnline`, which this shares.
 */
export interface NetStatus {
  online: boolean;
}

/**
 * The offline value, shared rather than rebuilt.
 *
 * Frozen because it is a singleton handed to every consumer: one caller writing
 * `status.online = true` into it would silently flip the answer for every screen
 * holding the same object, and the failure would look like a connectivity bug
 * rather than a mutation.
 *
 * It is also the value to assume before anything has been measured. Starting
 * from "offline" makes the app under-claim for a frame; starting from "online"
 * would make it over-claim, which is the direction that lets a cycle begin on a
 * device that has no network at all.
 */
export const OFFLINE: NetStatus = Object.freeze({ online: false });

/**
 * The string key for the current connection's label. Port of Android's
 * `NetworkStatus.displayTitle()`, collapsed to the two outcomes this platform
 * can actually distinguish.
 *
 * Returns a **key**, never a sentence. The rule itself is pure NetStatus logic
 * and belongs beside the type it reads, but this module — like everything in
 * `src/lib` — holds no user-facing text: a translated string here would need
 * `t`, and `t` lives behind a React context in a module tree this one must not
 * depend on. Callers write `t(networkTitleKey(status))`.
 *
 * The return type is spelled out rather than imported as `StringKey` for the
 * same reason: `src/i18n` imports React. It is a subset of `StringKey`, so it
 * drops straight into `t()`, and a key that stopped existing would fail to
 * compile at every call site.
 */
export type NetworkTitleKey = 'ios_net_online' | 'net_offline';

export function networkTitleKey(status: NetStatus): NetworkTitleKey {
  return status.online ? 'ios_net_online' : 'net_offline';
}
